"use client";

import { observer } from "mobx-react-lite";
import React, { useMemo } from "react";

import { useSmoothLoading } from "../hooks/useSmoothLoading";

import { useCommandMenuStore } from "./command-menu-context";
import { EmptyState } from "./EmptyState";
import useNavigateOnSelect from "./hooks/useNavigateOnSelect";
import { LoadingState } from "./LoadingState";
import { SearchHeader } from "./SearchHeader";
import { SearchResults } from "./SearchResults";
import { SpotlightProps } from "./types";

import { CommandDialog } from "@/components/ui/command";
import { Entity, useListFolderEntities } from "@/queries/folder-entities";

const CommandMenuContent: React.FC<{
  shouldShowLoading: boolean;
  showResults: boolean;
  searchQuery: string;
  showNoResults: boolean;
  showDefault: boolean;
  groupedEntities: unknown;
  totalResults: number;
  onEntitySelect: (entity: unknown) => void;
}> = ({
  shouldShowLoading,
  showResults,
  searchQuery,
  showNoResults,
  showDefault,
  groupedEntities,
  totalResults,
  onEntitySelect,
}) => {
  if (shouldShowLoading) {
    return <LoadingState />;
  }

  if (showResults) {
    return (
      <SearchResults
        groupedEntities={groupedEntities as Record<string, Entity[]>}
        totalResults={totalResults}
        onEntitySelect={onEntitySelect}
      />
    );
  }

  return (
    <EmptyState searchQuery={searchQuery} showNoResults={showNoResults} showDefault={showDefault} />
  );
};

export const CommandMenu: React.FC<SpotlightProps> = observer(() => {
  const store = useCommandMenuStore();

  const { data, isLoading: isApiLoading } = useListFolderEntities({
    search_query: store.debouncedSearchQuery,
    flatten: !!store.debouncedSearchQuery,
    enabled: store.isOpen,
    keepPreviousData: false,
    folder_id: null,
  });

  useNavigateOnSelect();

  const groupedEntities = useMemo(() => {
    const entities = data?.entities || [];
    return store.groupEntities(entities);
  }, [data?.entities, store]);

  const totalResults = useMemo(() => {
    const entities = data?.entities || [];
    return entities.length;
  }, [data?.entities]);
  const hasSearchQuery = store.searchQuery.length > 0;

  const isActuallyLoading =
    (hasSearchQuery && (store.isPendingDebounce || isApiLoading)) ||
    (!hasSearchQuery && isApiLoading);

  const { shouldShowLoading } = useSmoothLoading(isActuallyLoading, 200);

  const showNoResults = hasSearchQuery && !shouldShowLoading && totalResults === 0;
  const showResults = !shouldShowLoading && totalResults > 0;
  const showDefault = !hasSearchQuery && !shouldShowLoading && totalResults > 0;

  return (
    <CommandDialog open={store.isOpen} onOpenChange={open => store.handleOpenChange(open)}>
      <SearchHeader
        searchQuery={store.searchQuery}
        onSearchChange={value => store.setSearchQuery(value)}
        onClear={() => store.clearSearch()}
        isLoading={shouldShowLoading}
      />

      <CommandMenuContent
        shouldShowLoading={shouldShowLoading}
        showResults={showResults}
        searchQuery={store.searchQuery}
        showNoResults={showNoResults}
        showDefault={showDefault}
        groupedEntities={groupedEntities}
        totalResults={totalResults}
        onEntitySelect={entity => store.handleEntitySelect(entity as Entity)}
      />
    </CommandDialog>
  );
});

export { CommandMenuProvider, useCommandMenuStore } from "./command-menu-context";
